/**
 * @file Submission UI — SubmissionDetailLayout.
 * Shared layout for Admin Submission Detail and Expert Review Submission pages.
 * Renders: banner, back button header, summary card, comments card,
 * form stages (read-only), review & flag history, and optional extra sections.
 */

import React from "react";
import SubmissionStagesFormReadonly from "./SubmissionStagesFormReadonly";
import {
  formatReviewStatusLabel,
  normalizeReviewStatus,
} from "../../constants/reviewStatus";

type AnyObj = { [k: string]: any };

const ReadOnlyField: React.FC<{ label: string; value?: any }> = ({ label, value }) => {
  const v =
    value === null || typeof value === "undefined"
      ? ""
      : typeof value === "string"
        ? value
        : typeof value === "number"
          ? String(value)
          : typeof value === "boolean"
            ? value ? "Yes" : "No"
            : Array.isArray(value)
              ? value.length ? JSON.stringify(value) : ""
              : typeof value === "object"
                ? JSON.stringify(value)
                : String(value);

  return (
    <div className="ro-field">
      <label>{label}</label>
      <input value={v} readOnly />
    </div>
  );
};

function isInternalId(v: any): boolean {
  if (v == null || typeof v !== "string") return false;
  const s = String(v).trim();
  if (s.length < 16) return false;
  if (/^c[a-z0-9]{24}$/.test(s)) return true;
  if (/^[a-f0-9]{24}$/i.test(s)) return true;
  if (/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(s)) return true;
  return false;
}

function formatValue(v: any) {
  if (v === null || v === undefined) return "-";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string" && v.trim() === "") return "-";
  if (typeof v === "string" && isInternalId(v)) return "-";
  if (Array.isArray(v)) return v.length === 0 ? "-" : JSON.stringify(v);
  if (typeof v === "object") {
    if (v.name != null || v.email != null) {
      const name = v.name ?? v.username ?? "";
      const email = v.email ?? "";
      if (name && email) return `${name} (${email})`;
      if (name) return String(name);
      if (email) return email;
    }
    if (v instanceof Date || (typeof v?.toISOString === "function")) {
      return new Date(v).toLocaleString();
    }
    return JSON.stringify(v);
  }
  return String(v);
}

function formatSubmittedDate(sub: AnyObj | null | undefined): string | null {
  if (!sub) return null;
  const raw = sub.submittedDate ?? sub.submitted_date;
  if (raw == null || raw === "") return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

function buildCommentItems(submission: AnyObj): Array<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  const push = (label: string, v: any) => {
    const s = typeof v === "string" ? v.trim() : "";
    if (!s) return;
    out.push({ label, value: s });
  };

  push("Comment", submission.comment);
  push("Comments", submission.comments);
  push("Review comment", submission.reviewComment);
  push("Review comments", submission.reviewComments);
  push("Admin comment", submission.adminComment);
  push("Expert comment", submission.expertComment);
  push("Reason", submission.reason);
  push("Flag reason", submission.flagReason);
  push("Flagged reason", submission.flaggedReason);
  push("Review reason", submission.review?.reason);
  push("Review comment", submission.review?.comment);
  push("Flag reason", submission.flag?.reason);
  push("Flag comment", submission.flag?.comment);
  push("Admin review reason", submission.adminReview?.reason);
  push("Admin review comment", submission.adminReview?.comment);
  push("Background review comment", submission.background?.reviewComment);
  push("Background review reason", submission.background?.reviewReason);

  const seen = new Set<string>();
  return out.filter((it) => {
    const key = `${it.label}::${it.value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

type HistoryEntry = { raw: any; kind: "review" | "flag" };

function buildHistoryEntries(submission: AnyObj, extraReviewHistory?: any[]): HistoryEntry[] {
  const reviewHistory = [
    ...(extraReviewHistory ?? []),
    ...(Array.isArray(
      submission.reviews ?? submission.reviewHistory ?? submission.reviewLogs ?? submission.reviewLog
    )
      ? (submission.reviews ?? submission.reviewHistory ?? submission.reviewLogs ?? submission.reviewLog)
      : []),
  ];

  const flagHistory = Array.isArray(
    submission.flags ?? submission.flagHistory ?? submission.flagLogs ?? submission.flagLog
  )
    ? (submission.flags ?? submission.flagHistory ?? submission.flagLogs ?? submission.flagLog)
    : [];

  // Deduplicate reviews
  const seenReview = new Set<string>();
  const uniqueReviews = reviewHistory.filter((r: any) => {
    const key = String(r?.id ?? `${r?.expertId ?? ""}-${r?.createdAt ?? ""}-${r?.status ?? ""}`);
    if (seenReview.has(key)) return false;
    seenReview.add(key);
    return true;
  });

  const entries: HistoryEntry[] = [
    ...uniqueReviews.map((r: any) => ({ raw: r, kind: "review" as const })),
    ...flagHistory.map((f: any) => ({ raw: f, kind: "flag" as const })),
  ];

  return entries.sort((a, b) => {
    const ta = a.raw.createdAt ?? a.raw.created_at ?? a.raw.reviewedAt ?? "";
    const tb = b.raw.createdAt ?? b.raw.created_at ?? b.raw.reviewedAt ?? "";
    return new Date(tb).getTime() - new Date(ta).getTime();
  });
}

/** Type definition — Submission Detail Layout Props. */
export type SubmissionDetailLayoutProps = {
  bannerTitle: string;
  submission: AnyObj;
  /** Back button handler — header is hidden when not provided and no headerActions. */
  onBack?: () => void;
  /** Actions rendered in the header (e.g. Flag Dataset, Approve buttons) */
  headerActions?: React.ReactNode;
  /** Extra review history fetched separately (merged with submission's embedded history) */
  extraReviewHistory?: any[];
  /** When false, hides the Review & Flag History section (e.g. contributor submitted dataset view). */
  showReviewFlagHistory?: boolean;
  /** When false, hides Review Status in the summary (e.g. contributor submitted dataset view). */
  showReviewStatus?: boolean;
  /** Additional sections rendered after Review & Flag History */
  children?: React.ReactNode;
};

const SubmissionDetailLayout: React.FC<SubmissionDetailLayoutProps> = ({
  bannerTitle,
  submission,
  onBack,
  headerActions,
  extraReviewHistory,
  showReviewFlagHistory = true,
  showReviewStatus = true,
  children,
}) => {
  const reviewStatusRaw = submission?.reviewStatus ?? submission?.review_status ?? "PENDING";
  const reviewStatusNorm = normalizeReviewStatus(reviewStatusRaw);
  const submittedAtDisplay = formatSubmittedDate(submission);

  const topFields: Record<string, string | null> = {
    "Submission Code": submission.submissionCode,
    ...(showReviewStatus
      ? { "Review Status": formatReviewStatusLabel(reviewStatusNorm) }
      : {}),
    "Submitted date": submittedAtDisplay ?? "—",
    "Created At": submission.createdAt ? new Date(submission.createdAt).toLocaleString() : null,
    "Updated At": submission.updatedAt ? new Date(submission.updatedAt).toLocaleString() : null,
    "Current Stage": submission.currentStage ?? "-",
  };

  const commentItems = buildCommentItems(submission);
  const historyEntries = buildHistoryEntries(submission, extraReviewHistory);

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">{bannerTitle}</span>
      </div>
      <div className="admin-detail-root">
        {(onBack || headerActions) && (
          <div className="admin-detail-head">
            {onBack && (
              <button
                type="button"
                className="back-btn"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onBack();
                }}
              >
                ← Back
              </button>
            )}
            {headerActions && <div className="header-actions">{headerActions}</div>}
          </div>
        )}

        <div className="admin-detail-body dataset-container">
          {/* Summary */}
          <div className="dataset-section">
            <h3 className="dataset-section-title">Summary</h3>
            <div className="dataset-grid">
              {Object.entries(topFields).map(([label, value]) => (
                <ReadOnlyField key={`summary-${label}`} label={label} value={formatValue(value)} />
              ))}
            </div>
          </div>

          {/* Comments */}
          {commentItems.length > 0 && (
            <div className="dataset-section">
              <h3 className="dataset-section-title">Comments</h3>
              <div className="dataset-grid">
                {commentItems.map((c, idx) => (
                  <ReadOnlyField key={`comment-${idx}`} label={c.label} value={c.value} />
                ))}
              </div>
            </div>
          )}

          {/* Form Stages */}
          <SubmissionStagesFormReadonly submission={submission} />

          {/* Review & Flag History */}
          {showReviewFlagHistory && (
          <div className="dataset-section">
            <h3 className="dataset-section-title">Review &amp; Flag History</h3>
            {historyEntries.length === 0 ? (
              <div className="mini-value empty-data-note">No review or flag activity yet.</div>
            ) : (
              <div className="rh-timeline">
                {historyEntries.map((entry, i) => {
                  const { raw, kind } = entry;
                  const ts =
                    raw.createdAt ?? raw.created_at ?? raw.reviewedAt ?? raw.updatedAt ?? raw.updated_at;

                  const reviewer = raw.expert ?? raw.reviewer ?? raw.admin ?? raw.user ?? null;
                  const authorName =
                    reviewer?.name ?? raw.expertName ?? raw.reviewerName ?? raw.adminName;
                  const authorEmail =
                    reviewer?.email ?? raw.expertEmail ?? raw.reviewerEmail;

                  const status = raw.status ?? raw.reviewStatus ?? raw.flagType ?? raw.flagKind;
                  const entryComment =
                    raw.comment !== null && raw.comment !== undefined
                      ? raw.comment
                      : (raw.reason ?? raw.note ?? null);

                  const initials = authorName
                    ? authorName.split(" ").map((w: string) => w[0]).slice(0, 2).join("").toUpperCase()
                    : kind === "flag" ? "FL" : "RV";

                  return (
                    <div key={`entry-${i}`} className={`rh-item rh-item--${kind}`}>
                      <div className="rh-spine">
                        <div className="rh-avatar">{initials}</div>
                        {i < historyEntries.length - 1 && <div className="rh-line" />}
                      </div>

                      <div className="rh-body">
                        <div className="rh-header">
                          <div className="rh-identity">
                            <span className="rh-author">
                              {authorName ?? (kind === "flag" ? "Admin" : "Reviewer")}
                            </span>
                            {authorEmail && <span className="rh-email">{authorEmail}</span>}
                          </div>
                          <div className="rh-timestamp-block">
                            {status && (
                              <span
                                className={`rh-badge rh-badge--${String(status).toLowerCase().replace(/_/g, "-")}`}
                              >
                                {String(status).replace(/_/g, " ")}
                              </span>
                            )}
                            {ts && (
                              <>
                                <span className="rh-timestamp-date">
                                  {new Date(ts).toLocaleDateString(undefined, {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </span>
                                <span className="rh-timestamp-time">
                                  {new Date(ts).toLocaleTimeString(undefined, {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="rh-comment-block">
                          <span className="rh-comment-label">Comment</span>
                          {entryComment ? (
                            <p className="rh-comment-text">{entryComment}</p>
                          ) : (
                            <p className="rh-comment-empty">No comment provided.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Extra sections (e.g. Add review / comment) */}
          {children}
        </div>
      </div>
    </div>
  );
};

/** React component — Submission Detail Layout. */
export default SubmissionDetailLayout;
